import { ArrowRightOutlined, CloseOutlined } from '@ant-design/icons';
import { List } from 'antd';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import {
  dualESimOptions, eStorageOptions, licenseTypeOptions, statusOptions,
} from '../../../constants';
import { formatPascalCase, getEsimLicenses } from '../../../utils';
import { useMergeState } from '../../../utils/hooks';
import { toastrSuccess } from '../../../utils/toastNotification';
import NormalButton from '../../components/buttons/normalButton';
import NormalInput from '../../components/inputs/normalInput';
import InputTitle from '../../components/inputTitle';
import InstructionModal from '../../components/modals/instructionModal';
import NormalSelect from '../../components/selects/normalSelect';
import {
  createBatchEsims,
  generateEIDs, getForm, getSchema, isValidEID,
} from './helper';
import './style.scss';


const AddNewCard = (props) => {
  const history = useHistory();
  const [state, setState] = useMergeState({
    loading: false,
    eIDs: [],
    visible: false,
    erreIDs: [],
    modalLoading: false,
    esimLicenses: [],
    loadingLicenses: false,
  });
  const schema = getSchema();
  const form = getForm(schema);

  const statusOption = statusOptions;

  const [eID, firstEID, lastEID, status, licenseName] = form.watch(['eID', 'firstEID', 'lastEID', 'status', 'licenseName']);

  const toggleModal = () => {
    setState({ visible: !state.visible });
  };

  const onConfirm = async () => {
    const intersectionArray = state.eIDs.filter(value => state.erreIDs.includes(value));
    if (intersectionArray.length === state.eIDs.length) {
      toastrSuccess('No card added');
    } else {
      setState({ modalLoading: true });
      const addList = state.eIDs.filter(val => !intersectionArray.includes(val));
      const batchEsims = await createBatchEsims({ esimIds: addList, ...form.getValues() });
      if (!batchEsims?.isSuccess) {
        setState({ modalLoading: false });
        toggleModal();
        return;
      }
      toastrSuccess(`${state.eIDs.length - intersectionArray.length} ${state.eIDs.length - intersectionArray.length === 1 ? 'card' : 'cards'} added successfully`);
    }
    if (history.location?.state?.from) {
      history.goBack();
    } else {
      history.replace('/card-license');
    }
    toggleModal();
  };

  const onCancel = () => {
    toggleModal();
  };

  const onClickDelete = (item) => {
    setState({ eIDs: state.eIDs.filter(value => value !== item) });
  };

  const onAddEID = () => {
    const eID = form.getValues('eID');
    form.clearErrors('eID');
    if (eID.length < 32) {
      form.setError('eID', { message: 'The eID must be 32 digits. Please check again.' });
      return;
    }
    if (!isValidEID(eID)) {
      form.setError('eID', { message: 'eID is invalid. Please check again.' });
      return;
    }
    setState({ eIDs: _.uniq([...state.eIDs, eID]) });
    form.setValue('eID', '');
  };

  const onGenerateEID = () => {
    const { firstEID, lastEID } = form.getValues();
    if (firstEID.length < 30 || lastEID.length < 30) {
      form.setError('lastEID', { message: 'You must enter the first 30 digits of the first and the last eID of the batch.' });
      return;
    }
    if (BigInt(firstEID) >= BigInt(lastEID)) {
      form.setError('lastEID', { message: 'The first eID must be lower than the last one.' });
      return;
    }
    if (+((BigInt(lastEID) - BigInt(firstEID)).toString()) > 1000) {
      form.setError('lastEID', { message: 'Please divide the batch into smaller ones. Maximum 1000 eIDs per each.' });
      return;
    }
    if (+((BigInt(lastEID) - BigInt(firstEID)).toString()) + state.eIDs.length > 1000) {
      form.setError('lastEID', { message: 'The number of cards of the batch is higher than the number of cards that can be added to the list. Please add the smaller batch.' });
      return;
    }
    setState({ eIDs: _.uniq([...state.eIDs, ...generateEIDs(firstEID, lastEID)]) });
    form.setValue('firstEID', '');
    form.setValue('lastEID', '');
  };

  const onSave = async () => {
    if (state.eIDs.length > 1000) {
      form.setError('lastEID', { message: 'Cannot add more than 1000 eIDs each time.' });
      return;
    }
    const batchEsims = await createBatchEsims({ esimIds: state.eIDs, ...form.getValues() });
    if (!batchEsims?.isSuccess) {
      toggleModal();
      setState({ erreIDs: !_.isEmpty(batchEsims.esims) ? batchEsims.esims.map(esim => esim.id) : [] });
      return;
    }
    toastrSuccess(`${state.eIDs.length} ${state.eIDs.length === 1 ? 'card' : 'cards'} added successfully`);
    if (history.location?.state?.from) {
      history.goBack();
    } else {
      history.replace('/card-license');
    }
  };

  useEffect(() => {
    form.clearErrors('lastEID');
  }, [state.eIDs]);

  const initMasterData = async () => {
    setState({ loadingLicenses: true });
    const esimLicenses = await getEsimLicenses();
    if (esimLicenses.isSuccess) {
      const esimLicenseOptions = esimLicenses.licenses.map(license => ({
        label: license.name,
        value: license.id,
        data: {
          isAllowedDual: license.isAllowedDual,
          licenseType: license.licenseType,
          storage: license.storage,
        },
      }));
      setState({ esimLicenses: esimLicenseOptions });
    }
    setState({ loadingLicenses: false });
  };

  useEffect(() => {
    initMasterData();
  }, []);

  return (
    <div className="add-new-card">
      <div className="add-new-card-title">
        eSIM.me cards
      </div>
      <div className="add-new-card-row">
        <div className="add-new-card-row-col">
          <div className="flex">
            <NormalInput
              className="w-70"
              maxLength={32}
              isNumeric
              title="eID"
              name="eID"
              placeholder="eID"
              isUseForm
              control={form?.control}
              errMsg={form?.formState?.errors?.eID?.message}
            />
            <NormalButton
              className="ml-8"
              text="Add"
              type="primary"
              suffix={<ArrowRightOutlined />}
              disabled={!eID || state.eIDs.length === 1000}
              onClick={onAddEID}
            />
          </div>
          <div className="add-new-card-sub-title">
            Add a batch of eID
          </div>
          <NormalInput
            className="mt-8 w-70"
            maxLength={30}
            isNumeric
            name="firstEID"
            title="First eID in the batch"
            placeholder="First eID in the batch"
            isUseForm
            control={form?.control}
          />
          <div className="flex mt-32">
            <NormalInput
              className="w-70"
              maxLength={30}
              isNumeric
              title="Last eID in the batch"
              name="lastEID"
              placeholder="Last eID in the batch"
              isUseForm
              control={form?.control}
              errMsg={form?.formState?.errors?.lastEID?.message}
            />
            <NormalButton
              className="mt-18 ml-8"
              text="Generate"
              type="primary"
              suffix={<ArrowRightOutlined />}
              disabled={!firstEID || !lastEID || state.eIDs.length === 1000}
              onClick={onGenerateEID}
            />
          </div>
        </div>
        <div className="add-new-card-row-col">
          <InputTitle
            title={`Added eSIM.me cards (${state.eIDs.length}/1000)`}
            isRequired
          />
          <div className="add-new-card-row-col-esim-list">
            <List
              itemLayout="horizontal"
              dataSource={state.eIDs}
              renderItem={item => (
                <List.Item
                  actions={[
                    <CloseOutlined key="delete" className="icon icon-delete" onClick={() => onClickDelete(item)} />,
                  ]}
                >
                  {item}
                </List.Item>
              )}
            />
          </div>

        </div>
      </div>
      <div className="add-new-card-title mt-56">
        Status and license
      </div>
      <div className="add-new-card-row">
        <div className="add-new-card-row-col">
          <NormalSelect
            className="mt-10 w-50"
            title="Status"
            name="status"
            isRequired
            options={statusOption}
            placeholder="Status"
            isUseForm
            control={form?.control}
          />
          <NormalSelect
            className="mt-10 w-50"
            title="License name"
            name="licenseName"
            isRequired
            options={state.esimLicenses}
            loading={state.loadingLicenses}
            placeholder="License name"
            isObject
            isUseForm
            control={form?.control}
            disabled={state.loadingLicenses || status?.value}
          />

          <NormalInput
            className="mt-10 w-50"
            title="eStorage"
            name="eStorage"
            placeholder="eStorage"
            value={licenseName?.data.storage}
            disabled
          />
          <NormalInput
            className="mt-10 w-50"
            title="License type"
            name="licenseType"
            placeholder="License type"
            value={licenseName ? formatPascalCase(licenseName?.data.licenseType) : undefined}
            disabled
          />
          <NormalInput
            className="mt-10 w-50"
            title="Dual-eSIM"
            name="dualEsim"
            placeholder="Dual-eSIM"
            value={licenseName ? licenseName?.data?.isAllowedDual ? 'Allowed' : 'Not allowed' : undefined}
            disabled
          />
        </div>
      </div>
      <div className="add-new-card-footer mt-32">
        <NormalButton
          text="Save"
          type="primary"
          disabled={!form?.formState.isValid || _.isEmpty(state.eIDs)}
          onClick={onSave}
        />
      </div>
      <InstructionModal
        visible={state.visible}
        title="Warning"
        description={(
          <>
            <p>
              There are some eIDs that already existed. Please remind that they will not be added this time.
            </p>
            <p> The list of existed eIDs:</p>
          </>
          )}
        onCancel={onCancel}
        onConfirm={onConfirm}
        esims={state.erreIDs}
        loading={state.modalLoading}
      />
    </div>
  );
};

export default AddNewCard;
